import { useMemo } from 'react';
import { Edge, Node } from '@xyflow/react';

import {
  ConditionalStep,
  ForLoopStep,
  Step,
  StepType,
  WorkflowAgent,
} from '@frontend/types/workflow-agent';

import { generateConditionNodeId, generateEdgeId } from '../services/workflow-id-utils';

function isForLoopStepData(step: Step | ForLoopStep): step is ForLoopStep {
  return (
    (step as ForLoopStep).type === StepType.FOR_LOOP &&
    typeof step === 'object' &&
    step !== null &&
    'items_source' in (step as ForLoopStep) &&
    'item_name' in (step as ForLoopStep)
  );
}

function createStandardEdge(
  id: string,
  source: string,
  target: string,
  sourceHandle?: string,
  targetHandle?: string
): Edge {
  return {
    id,
    source,
    target,
    sourceHandle,
    targetHandle,
    deletable: true,
    type: 'custom',
  };
}

type MetadataNode = Partial<Node> & { id: string };

type MetadataEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
};

type WorkflowMetadata = {
  nodes: MetadataNode[];
  edges?: MetadataEdge[];
};

function createTriggerNodes(
  triggers: WorkflowAgent['triggers'],
  getMetadataNode: (nodeId: string) => MetadataNode | undefined
): Node[] {
  const nodes: Node[] = [];

  if (!triggers || triggers.length === 0) {
    return nodes;
  }

  triggers.forEach(trigger => {
    const triggerNodeId = trigger.id;
    const triggerMetadata = getMetadataNode(triggerNodeId);

    const triggerNode: Node = {
      id: triggerNodeId,
      type: trigger.type,
      position: triggerMetadata?.position || { x: 0, y: 0 },
      data: trigger as unknown as Record<string, unknown>,
    };

    if (triggerMetadata?.measured) {
      triggerNode.measured = triggerMetadata.measured;
    }

    nodes.push(triggerNode);
  });

  return nodes;
}

function createStepNode(
  step: Step,
  getMetadataNode: (nodeId: string) => MetadataNode | undefined,
  parentId?: string
): Node {
  const metadataNode = getMetadataNode(step.step_id);
  const stepData = step as unknown as Record<string, unknown>;

  let position = metadataNode?.position || { x: 0, y: 0 };

  if (stepData.position && typeof stepData.position === 'object') {
    const stepPosition = stepData.position as { x: number; y: number };
    if (stepPosition.x !== undefined && stepPosition.y !== undefined) {
      position = stepPosition;
    }
  }

  if (parentId && position.x === 0 && position.y === 0) {
    position = {
      x: 40,
      y: 60,
    };
  }

  const stepNode: Node = {
    id: step.step_id,
    type: step.type,
    position,
    data: stepData,
  };

  if (parentId) {
    stepNode.parentId = parentId;
    stepNode.extent = 'parent';
  }

  if (metadataNode?.measured) {
    stepNode.measured = metadataNode.measured;
  }

  if (step.type === StepType.FOR_LOOP) {
    const savedWidth = metadataNode?.measured?.width || metadataNode?.width;
    const savedHeight = metadataNode?.measured?.height || metadataNode?.height;

    const width = savedWidth || (parentId ? 280 : 320);
    const height = savedHeight || (parentId ? 160 : 188);

    stepNode.style = { width, height };
    stepNode.width = width;
    stepNode.height = height;
  }

  return stepNode;
}

function createConditionalStepNodes(
  step: ConditionalStep,
  getMetadataNode: (nodeId: string) => MetadataNode | undefined,
  getMetadataEdge: (source: string, target: string) => MetadataEdge | undefined,
  nodes: Node[],
  edges: Edge[],
  parentId?: string
): void {
  step.conditions.forEach(condition => {
    const conditionNodeId = generateConditionNodeId(step.step_id, condition);
    const conditionMetadata = getMetadataNode(conditionNodeId);

    const conditionNode: Node = {
      id: conditionNodeId,
      type: 'condition',
      position: conditionMetadata?.position || { x: 0, y: 0 },
      data: {
        type: 'condition',
        name: `Condition: ${condition.match_type}`,
        matching_condition: condition.matching_condition,
        match_type: condition.match_type,
        label: `${condition.match_type}: ${condition.matching_condition}`,
      },
    };

    if (parentId) {
      conditionNode.parentId = parentId;
      conditionNode.extent = 'parent';
    }

    if (conditionMetadata?.measured) {
      conditionNode.measured = conditionMetadata.measured;
    }

    nodes.push(conditionNode);

    const edgeToCondition = getMetadataEdge(step.step_id, conditionNodeId);
    edges.push(
      createStandardEdge(
        generateEdgeId(),
        step.step_id,
        conditionNodeId,
        edgeToCondition?.sourceHandle,
        edgeToCondition?.targetHandle
      )
    );

    const edgeFromCondition = getMetadataEdge(conditionNodeId, condition.go_to_step_id);
    edges.push(
      createStandardEdge(
        generateEdgeId(),
        conditionNodeId,
        condition.go_to_step_id,
        edgeFromCondition?.sourceHandle,
        edgeFromCondition?.targetHandle
      )
    );
  });
}

function processSteps(
  steps: Record<string, Step>,
  getMetadataNode: (nodeId: string) => MetadataNode | undefined,
  getMetadataEdge: (source: string, target: string) => MetadataEdge | undefined,
  nodes: Node[],
  edges: Edge[],
  parentId?: string
): void {
  if (!steps || Object.keys(steps).length === 0) {
    return;
  }

  Object.values(steps).forEach(step => {
    const stepNode = createStepNode(step, getMetadataNode, parentId);
    nodes.push(stepNode);

    if (step.type === StepType.CONDITIONAL) {
      const conditionalStep = step as ConditionalStep;
      createConditionalStepNodes(
        conditionalStep,
        getMetadataNode,
        getMetadataEdge,
        nodes,
        edges,
        parentId
      );
    }

    if (step.type === StepType.FOR_LOOP && isForLoopStepData(step)) {
      const forLoopSteps = step.steps && typeof step.steps === 'object' ? step.steps : {};
      processSteps(forLoopSteps, getMetadataNode, getMetadataEdge, nodes, edges, step.step_id);
    }
  });

  Object.values(steps).forEach(step => {
    if (step.next_step_id && step.type !== StepType.CONDITIONAL) {
      if (steps[step.next_step_id]) {
        const metadataEdge = getMetadataEdge(step.step_id, step.next_step_id);
        edges.push(
          createStandardEdge(
            generateEdgeId(),
            step.step_id,
            step.next_step_id,
            metadataEdge?.sourceHandle,
            metadataEdge?.targetHandle
          )
        );
      }
    }
  });
}

function createTriggerToStartStepEdges(
  triggers: WorkflowAgent['triggers'],
  startStepId: string | undefined,
  getMetadataEdge: (source: string, target: string) => MetadataEdge | undefined,
  edges: Edge[]
): void {
  if (!startStepId || !triggers || triggers.length === 0) {
    return;
  }

  triggers.forEach(trigger => {
    const metadataEdge = getMetadataEdge(trigger.id, startStepId);
    edges.push(
      createStandardEdge(
        generateEdgeId(),
        trigger.id,
        startStepId,
        metadataEdge?.sourceHandle,
        metadataEdge?.targetHandle
      )
    );
  });
}

export function useWorkflowToGraph(
  steps: Record<string, Step>,
  triggers: WorkflowAgent['triggers'],
  start_step_id: string,
  metadata: WorkflowMetadata
): { nodes: Node[]; edges: Edge[] } {
  return useMemo(() => {
    const getMetadataNode = (nodeId: string): MetadataNode | undefined => {
      return metadata.nodes.find(node => node.id === nodeId);
    };

    const getMetadataEdge = (source: string, target: string): MetadataEdge | undefined => {
      return metadata.edges?.find(edge => edge.source === source && edge.target === target);
    };

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    if (!steps || !triggers) {
      return { nodes, edges };
    }

    const triggerNodes = createTriggerNodes(triggers, getMetadataNode);
    nodes.push(...triggerNodes);

    processSteps(steps, getMetadataNode, getMetadataEdge, nodes, edges);

    createTriggerToStartStepEdges(triggers, start_step_id, getMetadataEdge, edges);

    return { nodes, edges };
  }, [steps, triggers, start_step_id, metadata.nodes, metadata.edges]);
}
